/**
 * Add to Calendar's menu (Gabriel 2026-10-22; shared-client-screens D6, Decision B), one menu for
 * the Mini App and the studio website: Google Calendar and Outlook open the booking filled in on
 * their sites; Apple Calendar opens the booking's calendar file, which a Mac or iPhone opens
 * straight into its calendar. The trigger keeps the look of the row action it sits in.
 *
 * Props only. Each side passes how a link opens (`onOpen`: the Mini App's Telegram link opener;
 * left out on the website, where the choices are plain links to a new tab) and what Apple
 * Calendar does (`onApple`: both sides mint a calendar file ticket and open its address). The
 * website's piece contract names the parts through `parts`.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';

import css from './CalendarMenu.module.css';

/** Google Calendar's and Outlook's links for the booking. */
export interface CalendarChoices {
  readonly google: string;
  readonly outlook: string;
}

type PartAttrs = Readonly<Record<string, string>>;

/** Attribute bags for the menu's parts (the website's `data-part` names). */
export interface CalendarMenuParts {
  readonly trigger?: PartAttrs;
  readonly menu?: PartAttrs;
  readonly google?: PartAttrs;
  readonly outlook?: PartAttrs;
  readonly apple?: PartAttrs;
}

export interface CalendarMenuProps {
  choices: CalendarChoices;
  /** Apple Calendar: opens the booking's calendar file. */
  onApple: () => void;
  /** Opens a choice's link; left out, the choices are plain links to a new tab. */
  onOpen?: (url: string) => void;
  /** The trigger's class (the row action's look). */
  className: string;
  /** The trigger's accessible name when its children are an icon. */
  ariaLabel?: string;
  parts?: CalendarMenuParts;
  children: ReactNode;
}

export function CalendarMenu({ choices, onApple, onOpen, className, ariaLabel, parts = {}, children }: CalendarMenuProps) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const close = () => setOpen(false);

  // A tap elsewhere closes it (a phone's tap does not move focus, so blur alone is not enough).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node | null)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const link = (url: string, label: string, attrs: PartAttrs | undefined) => (
    <a
      role="menuitem"
      className={css.item}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        if (onOpen) {
          e.preventDefault();
          onOpen(url);
        }
        close();
      }}
      {...attrs}
    >
      {label}
    </a>
  );

  return (
    <span
      ref={wrap}
      className={css.wrap}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) close();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') close();
      }}
    >
      <button
        type="button"
        className={className}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        {...parts.trigger}
      >
        {children}
      </button>
      {open && (
        <span className={css.menu} role="menu" {...parts.menu}>
          {link(choices.google, 'Google Calendar', parts.google)}
          {link(choices.outlook, 'Outlook', parts.outlook)}
          <button
            type="button"
            role="menuitem"
            className={css.item}
            onClick={() => {
              onApple();
              close();
            }}
            {...parts.apple}
          >
            Apple Calendar
          </button>
        </span>
      )}
    </span>
  );
}
